import React, { useState, useEffect } from "react";
import "./findwords.css";
import { ArrowLeft } from "@mui/icons-material";

const gridSize = 6; // Adjusted for better display
const wordsList = [
  ["ROW", "CAT", "DOG", "BEE", "FOX"],
  ["LION", "TIGER", "BEAR", "DEER", "WOLF"],
  ["APPLE", "BANANA", "CHERRY", "GRAPE", "PEACH"],
];

const directions = [
  { dr: 0, dc: 1 }, // Horizontal
  { dr: 1, dc: 0 }, // Vertical
  { dr: 1, dc: 1 }, // Diagonal Down-Right
  { dr: -1, dc: 1 }, // Diagonal Up-Right
];

const generateGrid = (words) => {
  let grid = Array.from({ length: gridSize }, () => Array(gridSize).fill(null));
  let placedWords = [];
  let wordPositions = {};

  words.forEach((word) => {
    let placed = false;
    while (!placed) {
      let direction = directions[Math.floor(Math.random() * directions.length)];
      let startRow = Math.floor(Math.random() * gridSize);
      let startCol = Math.floor(Math.random() * gridSize);
      let positions = [];

      if (
        startRow + direction.dr * (word.length - 1) >= 0 &&
        startRow + direction.dr * (word.length - 1) < gridSize &&
        startCol + direction.dc * (word.length - 1) >= 0 &&
        startCol + direction.dc * (word.length - 1) < gridSize
      ) {
        let fits = true;
        for (let i = 0; i < word.length; i++) {
          if (
            grid[startRow + i * direction.dr][startCol + i * direction.dc] !==
            null
          ) {
            fits = false;
            break;
          }
        }
        if (fits) {
          for (let i = 0; i < word.length; i++) {
            grid[startRow + i * direction.dr][startCol + i * direction.dc] =
              word[i];
            positions.push([
              startRow + i * direction.dr,
              startCol + i * direction.dc,
            ]);
          }
          placedWords.push(word);
          wordPositions[word] = positions;
          placed = true;
        }
      }
    }
  });

  for (let r = 0; r < gridSize; r++) {
    for (let c = 0; c < gridSize; c++) {
      if (!grid[r][c]) {
        grid[r][c] = String.fromCharCode(65 + Math.floor(Math.random() * 26));
      }
    }
  }

  return { grid, placedWords, wordPositions };
};

const FindWords = () => {
  const [puzzleIndex, setPuzzleIndex] = useState(0);
  const [{ grid, placedWords, wordPositions }, setGridData] = useState(
    generateGrid(wordsList[puzzleIndex])
  );
  const [selectedLetters, setSelectedLetters] = useState("");
  const [selectedCells, setSelectedCells] = useState([]);
  const [foundWords, setFoundWords] = useState([]);

  const handleLetterClick = (letter, rowIndex, colIndex) => {
    const cellIndex = selectedCells.findIndex(
      ([r, c]) => r === rowIndex && c === colIndex
    );

    if (cellIndex !== -1) {
      // If cell is already selected, remove it and corresponding letter
      setSelectedCells((prevCells) =>
        prevCells.filter((_, i) => i !== cellIndex)
      );
      setSelectedLetters((prevLetters) =>
        prevLetters
          .split("")
          .filter((_, i) => i !== cellIndex)
          .join("")
      );
    } else {
      // Add new cell and letter
      setSelectedCells((prevCells) => [...prevCells, [rowIndex, colIndex]]);
      setSelectedLetters((prev) => prev + letter);
    }
  };

  useEffect(() => {
    if (
      placedWords.includes(selectedLetters) &&
      !foundWords.includes(selectedLetters)
    ) {
      alert(`Correct! You found "${selectedLetters}"`);
      setFoundWords((prev) => [...prev, selectedLetters]);
      setSelectedLetters("");
      setSelectedCells([]);
    }
  }, [selectedLetters, placedWords, foundWords]);

  const handleNextPuzzle = () => {
    let nextIndex = (puzzleIndex + 1) % wordsList.length;
    setPuzzleIndex(nextIndex);
    setGridData(generateGrid(wordsList[nextIndex]));
    setFoundWords([]);
    setSelectedLetters("");
    setSelectedCells([]);
  };

  return (
    <div className="full-background">
      {" "}
      <button
        style={{
          position: "absolute",
          top: "16px",
          left: "16px",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          padding: "6px 12px",
          backgroundColor: "#e5e7eb",
          color: "#374151",
          borderRadius: "12px",
          border: "none",
          cursor: "pointer",
          transition: "all 0.2s ease-in-out",
        }}
      >
        <a
          href="/games"
          style={{
            display: "flex",
            alignItems: "center",
            textDecoration: "none",
            color: "inherit",
          }}
        >
          <ArrowLeft size={20} />
          <span style={{ fontSize: "16px", fontWeight: "500" }}>Back</span>
        </a>
      </button>
      {/* Wrap everything in this div */}
      <div className="word-search-container">
        <div className="game-layout">
          <div className="word-list">
            <h2>Find the Words:</h2>
            <ul>
              {placedWords
                .filter((w) => !foundWords.includes(w))
                .map((word, index) => (
                  <li key={index}>{word}</li>
                ))}
            </ul>
          </div>
          <div className="grid-container">
            <div className="grid">
              {grid.map((row, rowIndex) => (
                <div key={rowIndex} className="row">
                  {row.map((letter, colIndex) => {
                    const isSelected = selectedCells.some(
                      ([r, c]) => r === rowIndex && c === colIndex
                    );
                    const isFound = Object.values(wordPositions).some(
                      (positions) =>
                        positions.some(
                          ([r, c]) =>
                            r === rowIndex &&
                            c === colIndex &&
                            foundWords.some((word) =>
                              wordPositions[word].some(
                                ([rw, cw]) => rw === r && cw === c
                              )
                            )
                        )
                    );

                    return (
                      <button
                        key={colIndex}
                        className={`cell ${isSelected ? "selected" : ""} ${
                          isFound ? "found" : ""
                        }`}
                        onClick={() =>
                          handleLetterClick(letter, rowIndex, colIndex)
                        }
                      >
                        {letter}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
          <div className="found-words">
            <h2>Found Words:</h2>
            <p>{foundWords.join(", ") || "None"}</p>
          </div>
        </div>
        {foundWords.length === placedWords.length && (
          <button className="next-puzzle-button" onClick={handleNextPuzzle}>
            Next Puzzle
          </button>
        )}
      </div>
    </div>
  );
};

export default FindWords;
